<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="800">
        <v-card rounded="xl">
            <v-card-title class="d-flex justify-space-between align-center pa-4 pb-0">
                <div>
                    <h4 class="text-h6 font-weight-bold">{{ title }}</h4>
                    <p class="text-body-2 text-medium-emphasis mb-0">{{ subtitle }}</p>
                </div>
                <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue', false)"/>
            </v-card-title>

            <v-divider class="mt-3"/>

            <v-card-text class="pa-0">
                <v-stepper v-model="activeStep" alt-labels flat>
                    <v-stepper-header class="elevation-0">
                        <v-stepper-item :value="1" title="Name & Type"
                                        :complete="activeStep > 1" icon="mdi-pencil"/>
                        <v-divider/>
                        <v-stepper-item :value="2" title="Alert Rules"
                                        :complete="activeStep > 2" icon="mdi-bell-ring"/>
                        <v-divider/>
                        <v-stepper-item :value="3" title="Devices"
                                        :complete="activeStep > 3" icon="mdi-radar"/>
                        <v-divider/>
                        <v-stepper-item :value="4" title="Schedule"
                                        :complete="activeStep > 4" icon="mdi-clock-outline"/>
                        <v-divider/>
                        <v-stepper-item :value="5" title="Recipients"
                                        :complete="activeStep > 5" icon="mdi-account-group"/>
                    </v-stepper-header>

                    <v-divider/>

                    <v-stepper-window style="min-height: 320px; max-height: 50vh; overflow-y: auto">
                        <v-stepper-window-item :value="1">
                            <!-- Lock the type on edit too: an existing rule's kind
                                 cannot change, so the type picker is hidden. -->
                            <chose-type v-if="activeStep === 1" :name="name" :type="templateType" :lock-type="!!presetType || isEditing"
                                        @setName="(val) => name = val"
                                        @setType="(type) => templateType = type"/>
                        </v-stepper-window-item>

                        <v-stepper-window-item :value="2">
                            <template-details v-if="activeStep === 2" :data="alertInfo" :type="templateType"
                                              @setTemplate="(temp) => alertInfo = temp"/>
                        </v-stepper-window-item>

                        <v-stepper-window-item :value="3">
                            <div class="pa-4">
                                <div class="text-center mb-4">
                                    <v-icon size="36" color="primary" class="mb-2">mdi-radar</v-icon>
                                    <h3 class="text-subtitle-1 font-weight-bold">Select Trackers</h3>
                                    <p class="text-body-2 text-medium-emphasis">Choose which trackers this applies to</p>
                                </div>

                                <v-alert v-if="!devices || !devices.length" type="info" variant="tonal"
                                         density="compact" class="mb-3">
                                    No devices registered yet. Register a device first.
                                </v-alert>
                                <template v-else>
                                    <v-switch v-model="allTrackers" color="primary" density="comfortable"
                                              hide-details label="Applies to all trackers" class="mb-1"
                                              @update:model-value="onAllTrackersToggle"/>
                                    <p class="text-caption text-medium-emphasis mb-3">
                                        {{ allTrackers ? 'This alert applies to all your trackers.' : 'Pick the specific trackers this applies to.' }}
                                    </p>
                                    <v-expand-transition>
                                        <v-select v-if="!allTrackers" v-model="assetsId" :items="devices"
                                                  item-title="asset" item-value="asset_id"
                                                  chips label="Select tracker(s)" multiple variant="outlined">
                                            <template v-slot:item="{props, item}">
                                                <v-list-item :subtitle="item.raw.slno" v-bind="props"/>
                                            </template>
                                        </v-select>
                                    </v-expand-transition>
                                </template>
                            </div>
                        </v-stepper-window-item>

                        <v-stepper-window-item :value="4">
                            <schedule-details v-if="activeStep === 4" :data="scheduleInfo"
                                              @setSchedule="(schedule) => scheduleInfo = schedule"/>
                        </v-stepper-window-item>

                        <v-stepper-window-item :value="5">
                            <recipient-details v-if="activeStep === 5" :group="notificationGroup"
                                               @updateNotificationGroup="(group) => notificationGroup = group"/>
                        </v-stepper-window-item>
                    </v-stepper-window>
                </v-stepper>
            </v-card-text>

            <v-divider/>

            <v-card-actions class="pa-4 d-flex justify-space-between">
                <v-btn :disabled="activeStep === 1 || saving" variant="text" prepend-icon="mdi-arrow-left"
                       @click="prevStep">
                    Back
                </v-btn>
                <v-btn v-if="activeStep < 5" color="primary" append-icon="mdi-arrow-right"
                       :disabled="processing" @click="nextStep">
                    Continue
                </v-btn>
                <v-btn v-else color="primary" prepend-icon="mdi-check"
                       :loading="saving" :disabled="saving" @click="nextStep">
                    {{ isEditing ? 'Update Config' : 'Save Config' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useAuthStore} from "@/stores/authStore";
import {apiErrorMessage} from "@/composables/useApiError";
import ChoseType from "@/components/dialogs/monitoring/stepper/ChoseType.vue";
import TemplateDetails from "@/components/dialogs/monitoring/stepper/TemplateDetails.vue";
import ScheduleDetails from "@/components/dialogs/monitoring/stepper/ScheduleDetails.vue";
import RecipientDetails from "@/components/dialogs/monitoring/stepper/RecipientDetails.vue";

const props = defineProps({
    modelValue: Boolean,
    template: {type: Object, default: null},
    // When set ('Geofence' | 'Speed'), the type is pre-chosen and the type
    // picker is hidden so each add flow (Safe Zone / Speed Alert) is simpler.
    presetType: {type: String, default: null},
    // Existing rule names (for duplicate prevention). Passed from Monitoring so
    // we do not silently create two zones called "Home".
    existingNames: {type: Array, default: () => []},
    // "Save current location as a Safe Zone" presets (from the Live popup):
    // pre-centre a default circle, pre-select the tracker, and suggest a name.
    presetCenter: {type: Object, default: null}, // {lat, lng}
    presetRadius: {type: Number, default: 150},  // metres
    presetAssetId: {type: [Number, String], default: null},
    presetName: {type: String, default: null},
});
// The schedule "specific period" toggles - if none is on and the rule is not
// always-active, the schedule would never fire (guarded in step-4 validation).
const SCHEDULE_SPECIFICS = ['start_date_enabled', 'end_date_enabled', 'weekdays_enabled',
    'months_enabled', 'days_enabled', 'hours_enabled', 'tod_enabled', 'interval_enabled'];
// Each enabled toggle requires its value(s) to be filled, else the rule could
// be saved enabled-but-empty (e.g. "Specific months" on with no month picked).
const SCHEDULE_REQUIRED = {
    start_date_enabled: ['start_date'],
    end_date_enabled: ['end_date'],
    weekdays_enabled: ['weekdays'],
    months_enabled: ['months'],
    days_enabled: ['days'],
    hours_enabled: ['hours'],
    tod_enabled: ['tod_start', 'tod_end'],
    interval_enabled: ['repeat_interval'],
};
const isBlank = (v) => v == null || v === '' || (Array.isArray(v) && !v.length);
const dialogSwitch = ref(props.modelValue);
const notificationGroup = ref(null);
const {showSnackbar} = useSnackbarStore();
const auth = useAuthStore();
// Default new alerts to Safe Zone (geofence) - the most common consumer alert;
// the user can switch to Speed in step 1.
const templateType = ref(props.presetType ?? 'Geofence');
const scheduleInfo = ref(null);
// Seed a default circle when launched from "Save Place" (new zone only).
const alertInfo = ref(
    (!props.template && props.presetCenter)
        ? {
            type: 0,
            center_lat: Number(props.presetCenter.lat),
            center_lon: Number(props.presetCenter.lng),
            radius: props.presetRadius,
            enter_or_exit: 3,
            // Match the defaults TemplateDetails uses, so saving straight from
            // the preset (without touching step 2) does not persist NULL debounce
            // params different from a normally-drawn zone.
            trigger_delay: 300,
            minimum_violation_time: 5,
        }
        : null
);
const processing = ref(false);
const saving = ref(false);
const assetsId = ref(props.presetAssetId != null ? [props.presetAssetId] : null);
const devices = ref(null);
// "Applies to all trackers" toggle: when on, assetsId is every device's asset.
// Default ON for a new alert (the common case - cover everyone) unless a specific
// tracker was preset; edit mode auto-detects it from the saved rule below.
const allTrackers = ref(props.presetAssetId == null);
const allAssetIds = () => (devices.value ?? []).map((d) => d.asset_id).filter((id) => id != null);
const onAllTrackersToggle = (val) => {
    if (val) assetsId.value = allAssetIds();
};
const activeStep = ref(1);
const name = ref(props.template?.alertable?.label ?? props.presetName ?? null);
const emits = defineEmits(['update:modelValue', 'addConfigTemplate', 'updateConfigTemplate']);

const isEditing = computed(() => !!props.template);

// Block creating a second rule with an existing name (case-insensitive).
// Editing and keeping the rule's own name is allowed.
const isDuplicateName = (candidate) => {
    const norm = (s) => (s ?? '').trim().toLowerCase();
    const target = norm(candidate);
    if (!target) return false;
    if (isEditing.value && target === norm(props.template?.alertable?.label)) return false;
    return (props.existingNames || []).some((n) => norm(n) === target);
};

const title = computed(() => {
    const prefix = isEditing.value ? 'Edit' : 'Create';
    switch (activeStep.value) {
        case 1: return `${prefix} Alert`;
        case 2: return templateType.value === 'Speed' ? 'Set Speed Limit' : 'Set up the Safe Zone';
        case 3: return 'Choose Trackers';
        case 4: return 'Set Schedule';
        case 5: return 'Who to Notify';
        default: return `${prefix} Alert`;
    }
});

const subtitle = computed(() => {
    switch (activeStep.value) {
        case 1: return 'Name your alert and pick a type';
        case 2: return templateType.value === 'Speed' ? 'Set the speed limit' : 'Draw the zone on the map';
        case 3: return 'Select which trackers this applies to';
        case 4: return 'Set when this alert is active';
        case 5: return 'Choose who gets notified';
        default: return '';
    }
});

onMounted(() => {
    processing.value = true;
    fetchDevices().then(() => {
        if (props.template) {
            const template = props.template;
            templateType.value = template.monitoring_alertable_type === 'App\\Models\\MonitoringGeofenceTemplate' ? 'Geofence' : 'Speed';
            const urlPrefix = templateType.value.toLowerCase() === 'geofence' ? 'monitoring/geofence' : 'monitoring/speed';

            fetchAlert(urlPrefix, template.monitoring_alertable_id).then(() => {
                if (props.template.assets) {
                    assetsId.value = props.template.assets.map(asset => asset.id);
                    // A set applies_to_all wins; otherwise fall back to the
                    // legacy "every device selected" inference (the column always
                    // exists post-migration, so keying on presence would hide
                    // pre-flag "all" rules - this keeps them showing as all, and
                    // saving then migrates them to the flag). Explicit value check
                    // (not JS truthiness) mirrors the app and never trips on a
                    // stringy '0'/'false'.
                    const flag = props.template.applies_to_all;
                    allTrackers.value = flag === true || flag === 1 || flag === '1'
                        || (allAssetIds().length > 0
                            && allAssetIds().every(id => assetsId.value.includes(id)));
                    // When it resolves to "all trackers", re-enumerate every
                    // CURRENT device (not the stored snapshot) so the hidden
                    // picker never leaves an empty/stale selection that would
                    // block step 3, and a save re-syncs the pivot to the roster.
                    if (allTrackers.value) assetsId.value = allAssetIds();
                }
                if (template.monitoring_time_schedule_id) {
                    fetchScheduleDetails(template.monitoring_time_schedule_id);
                }
            });

            notificationGroup.value = template.monitoring_notification_group_id ?? null;
        } else if (allTrackers.value) {
            // New alert defaulting to "all trackers": now that devices are loaded,
            // select every asset (the toggle handler only runs on user interaction).
            assetsId.value = allAssetIds();
        }
    }).finally(() => processing.value = false);
});

const prevStep = () => {
    if (activeStep.value > 1) activeStep.value--;
};

const nextStep = () => {
    let readyToProceed = true;
    switch (activeStep.value) {
        case 1:
            if (!name.value?.trim()) {
                readyToProceed = false;
                showSnackbar('Please enter a name for this rule', 'error');
            } else if (isDuplicateName(name.value)) {
                readyToProceed = false;
                showSnackbar('A zone or alert with this name already exists', 'error');
            }
            break;
        case 2:
            if (!alertInfo.value) {
                readyToProceed = false;
                showSnackbar('Please configure the alert settings', 'error');
            } else if (templateType.value.toLowerCase() === 'speed') {
                const hasEmpty = Object.keys(alertInfo.value).some(key => key !== 'id' && !alertInfo.value[key]);
                if (hasEmpty) {
                    readyToProceed = false;
                    showSnackbar('All speed settings are required', 'error');
                }
            } else {
                // Use == null (not falsy) so a circle on the equator/prime
                // meridian (lat or lng === 0) is still valid.
                if ((alertInfo.value.center_lat == null || alertInfo.value.center_lon == null)
                    && !alertInfo.value.polygon_coordinates?.length) {
                    readyToProceed = false;
                    showSnackbar('Please draw a shape on the map', 'error');
                }
            }
            break;
        case 3:
            if (!assetsId.value || !assetsId.value.length) {
                readyToProceed = false;
                showSnackbar('Please select at least one tracker', 'error');
            }
            break;
        case 4:
            if (!scheduleInfo.value) {
                readyToProceed = false;
                showSnackbar('Please configure the schedule', 'error');
            } else if (!scheduleInfo.value.all_times_enabled
                && !SCHEDULE_SPECIFICS.some((k) => scheduleInfo.value[k])) {
                // Master "Always active" off but no specific period chosen would
                // save a schedule that may never be active - block it.
                readyToProceed = false;
                showSnackbar('Turn on "Always active" or choose at least one active period', 'error');
            } else {
                // Every enabled specific must have its value(s) filled in.
                const incomplete = Object.entries(SCHEDULE_REQUIRED).some(([flag, fields]) =>
                    scheduleInfo.value[flag] && fields.some((f) => isBlank(scheduleInfo.value[f])));
                if (incomplete) {
                    readyToProceed = false;
                    showSnackbar('Please complete the schedule options you turned on', 'error');
                }
            }
            break;
        case 5:
            if (!notificationGroup.value) {
                readyToProceed = false;
                showSnackbar('Recipients group is required', 'error');
            } else {
                saveConfigTemplate();
            }
            return;
    }
    if (readyToProceed && activeStep.value < 5) activeStep.value++;
};

const fetchDevices = () => {
    return send('get', 'personal-trackers/simple-list')
        .then((response) => {
            devices.value = response.data.data;
        })
        .catch(() => {
            showSnackbar('Devices could not be loaded', 'error');
        });
};

const fetchAlert = (urlPrefix, id) => {
    return send('get', `${urlPrefix}-templates/${id}`)
        .then((response) => {
            if (response.data.success) {
                alertInfo.value = response.data.data;
                alertInfo.value.polygon_coordinates = response.data.data?.polygon_coordinates?.map(coordinate => ({
                    lat: coordinate.latitude,
                    lng: coordinate.longitude,
                })) ?? [];
            }
        })
        .catch((error) => {
            showSnackbar(error.response?.data?.message ?? 'Could not load alert details', 'error');
        });
};

const fetchScheduleDetails = (scheduleId) => {
    return send('get', `monitoring/time-schedules/${scheduleId}`)
        .then((response) => {
            if (response.data.success) {
                scheduleInfo.value = response.data.data.schedule;
            }
        })
        .catch((error) => {
            showSnackbar(error.response?.data?.message ?? 'Could not load schedule', 'error');
        });
};

const saveConfigTemplate = () => {
    saving.value = true;
    send('post', 'monitoring/configs/personal', {
        name: name.value,
        assets: assetsId.value,
        alert: alertInfo.value,
        type: templateType.value,
        template: props.template?.id,
        schedule: scheduleInfo.value,
        notificationGroup: notificationGroup.value,
        // Always sent (true/false) so toggling "all trackers" off clears the flag;
        // when true, a tracker added later inherits this rule.
        applies_to_all: allTrackers.value,
    })
        .then((response) => {
            if (response.data.success) {
                if (isEditing.value) {
                    emits('updateConfigTemplate', response.data.data);
                } else {
                    emits('addConfigTemplate', response.data.data);
                }
                showSnackbar(response.data.message);
                emits('update:modelValue', false);
            }
        })
        .catch((error) => {
            // Speed and no-show alerts are gated, so this catch can receive a
            // 402. The server's own sentence does not name which one was
            // refused and tells everyone to upgrade, including members who
            // cannot. apiErrorMessage covers both. Everything else still shows
            // the server message; a 429 now gets the shared throttle wording.
            showSnackbar(
                apiErrorMessage(error, 'Config could not be saved', {canUpgrade: auth.isCircleOwner}).message,
                'error',
            );
        })
        .finally(() => {
            saving.value = false;
        });
};
</script>
